import { useMemo } from "react";
import { LocalesList } from "../../../types/Locales";
import {
  collectLeafKeys,
  EditorQuickFiltersState,
  getNestedValue,
  isEmptyTranslation,
  TranslationRowModel,
} from "../utils/editorPresentationUtils";

interface UseEditorViewModelArgs {
  locales: LocalesList[];
  baselineLocales: LocalesList[];
  searchQuery: string;
  quickFilters: EditorQuickFiltersState;
}

export const useEditorViewModel = ({
  locales,
  baselineLocales,
  searchQuery,
  quickFilters,
}: UseEditorViewModelArgs) => {
  return useMemo(() => {
    if (!locales.length) return [] as TranslationRowModel[];

    const keys = collectLeafKeys(locales[0].content as Record<string, unknown>);
    const lowerSearch = searchQuery.trim().toLowerCase();

    const rows = keys
      .map<TranslationRowModel>((keyPath) => {
        const values = locales.map((locale) =>
          getNestedValue(keyPath, locale.content as Record<string, unknown>)
        );
        const baselineValues = baselineLocales.map((locale) =>
          getNestedValue(keyPath, locale.content as Record<string, unknown>)
        );
        const hasEmpty = values.some((value) => isEmptyTranslation(value));
        const hasMissing = hasEmpty;
        const hasChanged = values.some((value, index) => value !== baselineValues[index]);

        return {
          keyPath,
          values,
          hasEmpty,
          hasMissing,
          hasChanged,
        };
      })
      .filter((row) => {
        if (lowerSearch) {
          const haystack = `${row.keyPath} ${row.values.join(" ")}`.toLowerCase();
          if (!haystack.includes(lowerSearch)) return false;
        }
        if (quickFilters.showOnlyChanged && !row.hasChanged) return false;
        if (quickFilters.showEmpty && !row.hasEmpty) return false;
        if (quickFilters.showMissing && !row.hasMissing) return false;
        return true;
      });

    return rows;
  }, [locales, baselineLocales, searchQuery, quickFilters]);
};
